/**
 * SuggestionCard — displays a single PendingSuggestion with
 * Accept, Modify, and Reject actions.
 *
 * Uses inline styles so it renders correctly in any context (no Tailwind required).
 */

import React, { useState } from 'react';
import type { PendingSuggestion, SuggestionPayload } from '../types/suggestions';
import {
  parsePayload,
  validateSkillRatingPayload,
  validateSkillPackagePayload,
} from '../utils/suggestion-payload';

interface SuggestionCardProps {
  suggestion: PendingSuggestion;
  onAccept: (id: string) => Promise<void>;
  onModify: (id: string, payload: SuggestionPayload) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  /** Disable all actions — another suggestion is currently being processed */
  globallyDisabled?: boolean;
  /** Status message to show inline when this card is being processed */
  processingStatus?: string;
}

function formatSource(source: string): string {
  switch (source) {
    case 'mcp': return 'AI assistant';
    case 'github_sync': return 'GitHub Sync';
    case 'manager': return 'Manager';
    case 'peer': return 'Peer';
    default: return source;
  }
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <span style={{ display: 'flex', gap: 2 }} aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          aria-hidden="true"
          style={{ color: n <= rating ? '#f59e0b' : '#d1d5db', fontSize: '1rem', lineHeight: 1 }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

export function SuggestionCard({ suggestion, onAccept, onModify, onReject, globallyDisabled = false, processingStatus }: SuggestionCardProps) {
  const [busy, setBusy] = useState(false);
  const isDisabled = busy || globallyDisabled;
  const isProcessing = busy && !!processingStatus;
  const [error, setError] = useState<string | null>(null);
  const [showModify, setShowModify] = useState(false);
  const [modifyRating, setModifyRating] = useState<number | null>(null);

  let payload: SuggestionPayload | null = null;
  try {
    payload = parsePayload(suggestion.payload);
  } catch {
    // malformed payload — render with fallback
  }

  const isRating = suggestion.suggestionType === 'skill_rating';
  const isPackage = suggestion.suggestionType === 'skill_package';
  const ratingPayload = payload && validateSkillRatingPayload(payload) ? payload : null;
  const packagePayload = payload && validateSkillPackagePayload(payload) ? payload : null;

  async function handle(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  const createdAt = suggestion.createdAt
    ? new Date(suggestion.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const sourcePillStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center',
    padding: '0.15rem 0.5rem', borderRadius: '999px',
    fontSize: '0.7rem', fontWeight: 600,
    background: suggestion.source === 'mcp'
      ? '#eff6ff'
      : suggestion.source === 'github_sync'
        ? '#f0fdf4'
        : '#f3f4f6',
    color: suggestion.source === 'mcp'
      ? '#1d4ed8'
      : suggestion.source === 'github_sync'
        ? '#15803d'
        : '#374151',
    border: `1px solid ${
      suggestion.source === 'mcp'
        ? '#bfdbfe'
        : suggestion.source === 'github_sync'
          ? '#bbf7d0'
          : '#e5e7eb'
    }`,
  };

  return (
    <div style={{
      borderRadius: '0.75rem',
      border: '1px solid #e5e7eb',
      background: 'white',
      padding: '0.875rem 1rem',
      display: 'flex', flexDirection: 'column', gap: '0.625rem',
    }}>
      {/* Source + date row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={sourcePillStyle}>
          {suggestion.source === 'github_sync' ? '🐙' : '✨'} {formatSource(suggestion.source)}
        </span>
        {suggestion.suggesterName && (
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{suggestion.suggesterName}</span>
        )}
        {createdAt && (
          <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 'auto' }}>{createdAt}</span>
        )}
      </div>

      {/* Title + stars */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
          {isRating && ratingPayload ? (
            <>Suggested rating for <span style={{ color: '#2563eb' }}>{ratingPayload.skillName}</span></>
          ) : isPackage && packagePayload ? (
            <>Suggested package: <span style={{ color: '#2563eb' }}>{packagePayload.name}</span></>
          ) : (
            'Skill suggestion'
          )}
        </p>
        {isRating && ratingPayload && <RatingStars rating={ratingPayload.rating} />}
      </div>

      {/* Package details */}
      {isPackage && packagePayload && (
        <div style={{ fontSize: '0.8rem', color: '#374151', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {packagePayload.description && (
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.775rem' }}>{packagePayload.description}</p>
          )}
          {packagePayload.categories.map((cat) => (
            <div key={cat.name}>
              <span style={{ fontWeight: 600 }}>{cat.name}:</span>{' '}
              <span style={{ color: '#6b7280' }}>{cat.skills.join(', ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Reason */}
      {suggestion.reason && (
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
          &ldquo;{suggestion.reason}&rdquo;
        </p>
      )}

      {/* Inline modify form */}
      {showModify && isRating && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.8rem', color: '#374151' }} htmlFor={`modify-rating-${suggestion.id}`}>
            Adjust rating:
          </label>
          <select
            id={`modify-rating-${suggestion.id}`}
            value={modifyRating ?? ratingPayload?.rating ?? 3}
            onChange={(e) => setModifyRating(Number(e.target.value))}
            style={{ fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.25rem 0.5rem' }}
          >
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            onClick={() => {
              if (!payload || !isRating || modifyRating === null) return;
              const modified: SuggestionPayload = { ...(payload as object), rating: modifyRating } as SuggestionPayload;
              handle(() => onModify(suggestion.id, modified));
            }}
            disabled={isDisabled}
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', borderRadius: '6px', border: 'none', background: '#2563eb', color: 'white', fontWeight: 600, cursor: 'pointer', opacity: isDisabled ? 0.6 : 1 }}
          >
            Save
          </button>
          <button
            onClick={() => setShowModify(false)}
            disabled={isDisabled}
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#dc2626' }} role="alert">{error}</p>
      )}

      {/* Action buttons — replaced by inline status while processing */}
      {!showModify && (
        isProcessing && processingStatus ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            padding: '0.4rem 0',
          }}>
            <div style={{
              width: 16, height: 16, flexShrink: 0,
              border: '2px solid #c7d2fe',
              borderTop: '2px solid #4f46e5',
              borderRadius: '50%',
              animation: 'sc-spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.8rem', color: '#4338ca', fontWeight: 500 }}>
              {processingStatus}
            </span>
            <style>{`@keyframes sc-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => handle(() => onAccept(suggestion.id))}
              disabled={isDisabled}
              style={{ flex: 1, fontSize: '0.8rem', padding: '0.4rem 0', borderRadius: '0.5rem', border: 'none', background: '#2563eb', color: 'white', fontWeight: 600, cursor: 'pointer', opacity: isDisabled ? 0.6 : 1 }}
            >
              Accept
            </button>
            {isRating && (
              <button
                onClick={() => setShowModify(true)}
                disabled={isDisabled}
                style={{ flex: 1, fontSize: '0.8rem', padding: '0.4rem 0', borderRadius: '0.5rem', border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer' }}
              >
                Modify
              </button>
            )}
            <button
              onClick={() => handle(() => onReject(suggestion.id))}
              disabled={isDisabled}
              style={{ flex: 1, fontSize: '0.8rem', padding: '0.4rem 0', borderRadius: '0.5rem', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 600, cursor: 'pointer' }}
            >
              Reject
            </button>
          </div>
        )
      )}
    </div>
  );
}
